import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../../services/api';
import { 
  Calendar, 
  Clock,
  MapPin,
  ChevronRight,
  X,
  CheckCircle,
  AlertCircle
} from 'lucide-react';
import Navbar from '../../components/ALLJsx/Navbar';
import Footer from '../../components/ALLJsx/Footer';
import '../AllCss/Appointments.css';

function Appointments() {
  const user = useSelector((state) => state.auth.user);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('scheduled'); // 'scheduled', 'cancelled', 'completed'

  const fetchAppointments = async () => {
    if (!user) {
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/appointments`, {
        withCredentials: true,
              });
      
      const now = new Date();
      const appointmentsToMarkCompleted = [];
      
      // Process all appointments
      const processedAppointments = response.data.data.appointments.map(apt => {
        const appointmentDate = new Date(apt.appointmentDate);
        const isPast = appointmentDate <= now;
        
        // Mark past appointments as completed
        if (isPast && apt.status !== 'completed' && apt.status !== 'cancelled') {
          appointmentsToMarkCompleted.push(apt.id);
        }
        
        return {
          ...apt,
          appointmentDate,
          isPast,
        };
      });
      
      // Mark past appointments as completed in the background
      if (appointmentsToMarkCompleted.length > 0) {
        appointmentsToMarkCompleted.forEach(async (appointmentId) => {
          try {
            await axios.put(
              `${API_URL}/api/appointments/${appointmentId}`,
              { status: 'completed' },
              {
        withCredentials: true,
                              }
            );
          } catch (error) {
            console.error(`Error marking appointment ${appointmentId} as completed:`, error);
          }
        });
      }
      
      setAppointments(processedAppointments);
    } catch (error) {
      console.error('Error fetching appointments:', error);
      setAppointments([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAppointments();
    
    // Set up interval to check for past appointments every minute
    const interval = setInterval(() => {
      fetchAppointments();
    }, 60000);
    
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Filter appointments based on active filter
  const filteredAppointments = appointments.filter(apt => {
    if (activeFilter === 'scheduled') {
      return apt.status !== 'cancelled' && apt.status !== 'completed' && !apt.isPast;
    } else if (activeFilter === 'cancelled') {
      return apt.status === 'cancelled';
    } else if (activeFilter === 'completed') {
      return apt.status === 'completed' || apt.isPast;
    }
    return true;
  });

  // Format appointment for display
  const formatAppointment = (apt) => ({
    ...apt,
    id: apt.id,
    type: apt.type.replace('_', ' ').toUpperCase(),
    carDisplay: apt.car ? `${apt.car.year} ${apt.car.make} ${apt.car.model}` : 'General Consultation',
    date: apt.appointmentDate.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    }),
    time: apt.appointmentDate.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit'
    }),
    location: '6314 Washington Ave, Ocean Springs, MS 39564',
    status: apt.status,
  });

  const formattedAppointments = filteredAppointments
    .map(formatAppointment)
    .sort((a, b) => {
      // Sort upcoming by date ascending, past/canceled by date descending
      if (activeFilter === 'scheduled') {
        return a.appointmentDate - b.appointmentDate;
      } else {
        return b.appointmentDate - a.appointmentDate;
      }
    });

  return (
    <div className="Appointments">
      <Navbar />
      <div className="appointments-container">
        <div className="appointments-header">
          <h1>Appointment Management</h1>
          <p>View and manage your scheduled appointments</p>
        </div>

        {/* Filter Toggle Bar */}
        <div className="appointments-filters">
          <button
            className={`filter-btn ${activeFilter === 'scheduled' ? 'active' : ''}`}
            onClick={() => setActiveFilter('scheduled')}
          >
            <Calendar size={16} />
            Scheduled
            {appointments.filter(apt => apt.status !== 'cancelled' && apt.status !== 'completed' && !apt.isPast).length > 0 && (
              <span className="filter-count">
                {appointments.filter(apt => apt.status !== 'cancelled' && apt.status !== 'completed' && !apt.isPast).length}
              </span>
            )}
          </button>
          <button
            className={`filter-btn ${activeFilter === 'cancelled' ? 'active' : ''}`}
            onClick={() => setActiveFilter('cancelled')}
          >
            <X size={16} />
            Cancelled
            {appointments.filter(apt => apt.status === 'cancelled').length > 0 && (
              <span className="filter-count">
                {appointments.filter(apt => apt.status === 'cancelled').length}
              </span>
            )}
          </button>
          <button
            className={`filter-btn ${activeFilter === 'completed' ? 'active' : ''}`}
            onClick={() => setActiveFilter('completed')}
          >
            <CheckCircle size={16} />
            Completed
            {appointments.filter(apt => apt.status === 'completed' || apt.isPast).length > 0 && (
              <span className="filter-count">
                {appointments.filter(apt => apt.status === 'completed' || apt.isPast).length}
              </span>
            )}
          </button>
        </div>

        {/* Appointments List */}
        <div className="appointments-list">
          {loading ? (
            <div className="appointments-loading">
              <Calendar size={44} />
              <p>Retrieving appointment information...</p>
            </div>
          ) : formattedAppointments.length > 0 ? (
            formattedAppointments.map((apt) => (
              <div key={apt.id} className="appointment-card">
                <div className="appointment-card-left">
                  <div className="appointment-type-badge">{apt.type}</div>
                  <div className={`appointment-status-badge status-${apt.status}`}>
                    {apt.status === 'pending' && <AlertCircle size={12} />}
                    {apt.status === 'confirmed' && <CheckCircle size={12} />}
                    {apt.status === 'completed' && <CheckCircle size={12} />}
                    {apt.status === 'cancelled' && <X size={12} />}
                    <span>{apt.status.charAt(0).toUpperCase() + apt.status.slice(1)}</span>
                  </div>
                </div>
                
                <div className="appointment-card-content">
                  <h3 className="appointment-car">{apt.carDisplay}</h3>
                  
                  <div className="appointment-info">
                    <div className="appointment-info-item">
                      <Calendar size={15} />
                      <span>{apt.date}</span>
                    </div>
                    <div className="appointment-info-item">
                      <Clock size={15} />
                      <span>{apt.time}</span>
                    </div>
                    <div className="appointment-info-item">
                      <MapPin size={15} />
                      <span>{apt.location}</span>
                    </div>
                  </div>
                </div>

                {apt.car && (
                  <div className="appointment-card-right">
                    <Link to={`/cars/${apt.car.id}`} className="appointment-view-car">
                      View Details <ChevronRight size={14} />
                    </Link>
                  </div>
                )}
              </div>
            ))
          ) : (
            <div className="appointments-empty">
              <Calendar size={44} />
              <h3>No {activeFilter} appointments found</h3>
              <p>
                {activeFilter === 'scheduled' 
                  ? "You currently have no scheduled appointments."
                  : activeFilter === 'cancelled'
                  ? "You have no cancelled appointments on record."
                  : "You have no completed appointments on record."}
              </p>
              {activeFilter === 'scheduled' && (
                <Link to="/inventory" className="appointments-browse-btn">
                  View Available Inventory
                </Link>
              )}
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}

export default Appointments;